import React, { useState, useEffect } from 'react';
import { Text, Flex, Dialog, Button, TextArea, TextField } from "@radix-ui/themes";
import { mdiChevronDown, mdiChevronUp, mdiEye, mdiCreation, mdiDelete, mdiCached, mdiFeather, mdiContentSave, mdiClose } from '@mdi/js';
import { Icon } from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import avatar from '../assets/avatar2.png';
import { useStore } from '../store/store';

const ChapterSummary = ({ chapter, novel }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [state, actions] = useStore();
    
    // Add local chapter state to ensure updates are immediately visible
    const [localChapter, setLocalChapter] = useState(chapter);
    
    // New state for edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(chapter.title || '');
    const [editedContent, setEditedContent] = useState(chapter.content || '');
    const [isSaving, setIsSaving] = useState(false);

    // Add state for summary generation
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    // Update local chapter when the prop changes
    useEffect(() => {
        setLocalChapter(chapter);
    }, [chapter]);

    // If this chapter has been deleted, don't render anything
    if (isDeleted) {
        return null;
    }

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // Handle navigation to scenes page
    const handleViewScenes = () => {
        navigate(`/novel/${novel.id}/chapter/${chapter.id}`);
    };

    // Check if this chapter already has scenes
    const hasExistingScenes = chapter.scenes && chapter.scenes.length > 0;

    // Handle edit chapter button click
    const handleEditChapter = () => {
        setEditedTitle(chapter.title || '');
        setEditedContent(chapter.content || '');
        setIsEditing(true);
        setIsExpanded(true); // Always expand when editing
    };
    
    // Save edited chapter details
    const saveChanges = async () => {
        setIsSaving(true);
        try {
            // Update both title and content in the database
            await api.updateChapterDetails(chapter.id, editedTitle, editedContent);
            
            // Create a deep copy of the global state novel (not just the prop novel)
            const updatedStateNovel = JSON.parse(JSON.stringify(state.currentNovel));
            
            // Find and update the chapter in the global state copy
            const stateChapter = updatedStateNovel.chapters.find(c => c.id === chapter.id);
            if (stateChapter) {
                stateChapter.title = editedTitle;
                stateChapter.content = editedContent;
            }
            
            // Also update the novel passed as prop (for local rendering)
            const updatedPropNovel = JSON.parse(JSON.stringify(novel));
            const propChapter = updatedPropNovel.chapters.find(c => c.id === chapter.id);
            if (propChapter) {
                propChapter.title = editedTitle;
                propChapter.content = editedContent;
            }
            
            // Update the novel in the global state
            actions.setCurrentNovel(updatedStateNovel);
            
            // Update local chapter state to immediately reflect changes
            setLocalChapter({
                ...localChapter,
                title: editedTitle,
                content: editedContent
            });
            
            // Exit edit mode
            setIsEditing(false);
            
            console.log(`Chapter ${chapter.id} updated successfully in both database and state`);
        } catch (error) {
            console.error('Error saving chapter changes:', error);
            alert('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };
    
    // Dismiss changes without saving
    const dismissChanges = () => {
        setIsEditing(false);
    };

    const handleGenerateButtonClick = () => {
        if (isGenerating) return;
        
        // If there are existing scenes, show the confirmation modal
        if (hasExistingScenes) {
            setConfirmModalOpen(true);
        } else {
            // Otherwise, directly generate scenes
            generateScenes();
        }
    };

    const generateScenes = async () => {
        setIsGenerating(true);
        try {
            // Generate new scenes
            const result = await api.generateScenes(novel.id, chapter.id);
            
            if (result.success && result.scenes) {
                // Create a deep copy of the novel object
                const updatedNovel = JSON.parse(JSON.stringify(novel));
                
                // Find the chapter and update its scenes
                const chapterToUpdate = updatedNovel.chapters.find(c => c.id === chapter.id);
                if (chapterToUpdate) {
                    chapterToUpdate.scenes = result.scenes;
                    
                    // Update the novel in the store
                    actions.setCurrentNovel(updatedNovel);
                    
                    console.log(`Successfully generated ${result.scenes.length} scenes for chapter ${chapter.chapter_number}`);
                }
            } else {
                console.error('Scene generation failed:', result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error generating scenes:', error);
            // You could add toast notification for error here if you have a notification system
        } finally {
            setIsGenerating(false);
        }
    };
    
    const handleRegenerate = () => {
        // Close the modal immediately for better UX
        setConfirmModalOpen(false);
        // Then start generation
        generateScenes();
    };

    const handleDeleteButtonClick = () => {
        setDeleteModalOpen(true);
    };
    
    const deleteChapter = async () => {
        setIsDeleting(true);
        setDeleteModalOpen(false);
        
        try {
            const result = await api.deleteChapter(chapter.id);
            
            if (result.success) {
                // Mark as deleted to prevent rendering
                setIsDeleted(true);
                
                // Create a deep copy of the novel object
                const updatedNovel = JSON.parse(JSON.stringify(novel));
                
                // Remove the chapter from the novel
                updatedNovel.chapters = updatedNovel.chapters.filter(c => c.id !== chapter.id);
                
                // Update chapter numbers for remaining chapters
                updatedNovel.chapters.forEach((c, index) => {
                    c.chapter_number = index + 1;
                });
                
                // Update the novel in the store
                actions.setCurrentNovel(updatedNovel);
                
                // Dispatch a custom event to notify parent components
                window.dispatchEvent(new CustomEvent('chapterDeleted', { 
                    detail: { chapterId: chapter.id } 
                }));
                
                console.log(`Successfully deleted chapter ${chapter.chapter_number}`);
            } else {
                console.error('Chapter deletion failed:', result.error || 'Unknown error');
                // You could add toast notification for error here
            }
        } catch (error) {
            console.error('Error deleting chapter:', error);
            // You could add toast notification for error here
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle generate summary button click
    const handleGenerateSummary = async () => {
        if (isGeneratingSummary) return;
        
        setIsGeneratingSummary(true);
        try {
            // Generate summary from the current chapter content
            const apiKey = localStorage.getItem('openRouterApiKey');
            const result = await api.generateChapterSummary(chapter.id, apiKey);
            
            if (result.success) {
                // Update the local chapter state with the new summary
                setLocalChapter({
                    ...localChapter,
                    content: result.summary
                });
                
                // Create a deep copy of the novel object
                const updatedNovel = JSON.parse(JSON.stringify(novel));
                
                // Find the chapter and update its content with the summary
                const chapterToUpdate = updatedNovel.chapters.find(c => c.id === chapter.id);
                if (chapterToUpdate) {
                    chapterToUpdate.content = result.summary;
                    
                    // Update the novel in the store
                    actions.setCurrentNovel(updatedNovel);
                }
                
                console.log('Successfully generated summary for chapter');
            } else {
                console.error('Summary generation failed:', result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error generating chapter summary:', error);
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    return (
        <div>
            <div className='chapter-summary-header-wrapper'>
                {state.currentNovel?.chapters[0]?.scenes.length === 0 || state.currentNovel?.chapters[0]?.scenes[0]?.content === '' ?
                    <Flex className="tutorial-container" style={{ }}>
                        <Flex className={"tutorial-text"}>
                            <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                            <div>Let's generate some actual content!<br /><br />Hover over a chapter block and select 'View Scenes' to jump to the Scene View for that chapter.<br /><br />
                            {state.currentNovel?.type === 'fiction' ? '(If you want AI to outline the chapter, you can choose "Edit Chapter", enter a summary and then hit "Generate Scenes")'  : null}
                            </div>
                        </Flex>
                    </Flex> : null}
                <br />
                <Flex>
                    <div>
                        <Flex direction={'column'}>
                            {isEditing ? (
                                <>
                                    <Text className='chapter-summary-header-chapter-text'>Chapter {localChapter.chapter_number}</Text>
                                    <TextField.Root 
                                        className="sidebar-form-textInput" 
                                        style={{ width: '350px', marginTop: '20px' }}
                                        placeholder="Chapter title" 
                                        value={editedTitle} 
                                        variant="soft" 
                                        onChange={(e) => setEditedTitle(e.target.value)}
                                    />
                                </>
                            ) : (
                                <>
                                    <Text className='chapter-summary-header-chapter-text'>Chapter {localChapter.chapter_number}</Text>
                                    <Text className='chapter-summary-header-chapter-title'>{localChapter.title}</Text>
                                </>
                            )}
                        </Flex>
                    </div>
                    <div className='chapter-summary-header-info'>
                        <div className='chapter-summary-header-info-icon'>
                            <Icon path={mdiFeather} size={1} />{localChapter.words} words ({localChapter.reading_time_mins} min)
                        </div>
                    </div>
                </Flex>
            </div>
            <Flex className='chapter-summary-content-wrapper'>
                <div style={{width: '65%'}}>
                    {isEditing ? (
                        <TextArea
                            className="sidebar-form-textInput"
                            value={editedContent}
                            placeholder="Chapter content..."
                            variant="soft"
                            onChange={(e) => setEditedContent(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '200px',
                                padding: '10px',
                                fontFamily: 'Libre Baskerville',
                                backgroundColor: 'rgb(0 0 0 / 9%)'
                            }}
                        />
                    ) : (
                        <>
                            <div className={`chapter-summary-content ${isExpanded ? 'chapter-summary-content-extended' : ''}`}>
                                {localChapter.content}
                                {localChapter.content === '' || localChapter.content === null ? <div>Click 'Edit Chapter' to add a summary. The summary is used by the AI to generate scenes for the chapter.</div> : null}
                            </div>
                            <div className='chapter-summary-content-expand' onClick={toggleExpand}>
                                <Icon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={1} />
                            </div>
                        </>
                    )}
                </div>
                
                {isEditing ? (
                    <div className='chapter-summary-tools' style={{ opacity: 1 }}>
                        <Flex className='chapter-summary-tools-item' onClick={saveChanges} style={{ opacity: isSaving ? 0.6 : 1 }}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon 
                                    path={mdiContentSave} 
                                    size={1} 
                                    className={isSaving ? 'icon-spin' : ''}
                                    style={{
                                        marginTop: '6px', 
                                        marginLeft: '-13px', 
                                        transform: 'scale(0.8)'
                                    }}
                                />
                            </div>
                            <div className='chapter-summary-tools-text'>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </div>
                        </Flex>
                        <Flex className='chapter-summary-tools-item' onClick={dismissChanges}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon path={mdiClose} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                            </div>
                            <div className='chapter-summary-tools-text'>
                                Dismiss Changes
                            </div>
                        </Flex>
                    </div>
                ) : (
                    <div className='chapter-summary-tools'>
                        <Flex className='chapter-summary-tools-item' onClick={handleViewScenes}>
                            <div className='chapter-summary-tools-icon'>
                                <Icon path={mdiEye} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                            </div>
                            <div className='chapter-summary-tools-text'>
                                View Scenes
                            </div>
                        </Flex>
                        
                        {!state.readOnly && (
                            <>
                                <Flex className='chapter-summary-tools-item' onClick={handleEditChapter}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon path={mdiFeather} size={1} style={{marginTop: '6px', marginLeft: '-13px', transform: 'scale(0.8)'}}/>
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        Edit Chapter
                                    </div>
                                </Flex>
                                <Flex className='chapter-summary-tools-item' onClick={handleGenerateSummary}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon 
                                            path={isGeneratingSummary ? mdiCached : mdiCreation} 
                                            size={1} 
                                            className={isGeneratingSummary ? 'icon-spin' : ''}
                                            style={{
                                                marginTop: '6px', 
                                                marginLeft: '-14px', 
                                                transform: 'scale(0.8)'
                                            }}
                                        />
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
                                    </div>
                                </Flex>
                                {novel.type === 'fiction' &&
                                <Flex className='chapter-summary-tools-item' onClick={handleGenerateButtonClick}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon 
                                            path={isGenerating ? mdiCached : mdiCreation} 
                                            size={1} 
                                            className={isGenerating ? 'icon-spin' : ''}
                                            style={{
                                                marginTop: '6px', 
                                                marginLeft: '-14px', 
                                                transform: 'scale(0.8)'
                                            }}
                                        />
                                    </div>
                                    
                                        <div className='chapter-summary-tools-text'>
                                            {isGenerating ? 'Generating...' : (hasExistingScenes ? 'Re-generate Scenes' : 'Generate Scenes')}
                                        </div>
                                </Flex>
                                }
                                <Flex className='chapter-summary-tools-item' onClick={handleDeleteButtonClick}>
                                    <div className='chapter-summary-tools-icon'>
                                        <Icon 
                                            path={mdiDelete} 
                                            size={1} 
                                            className={isDeleting ? 'icon-spin' : ''}
                                            style={{
                                                marginTop: '6px', 
                                                marginLeft: '-13px', 
                                                transform: 'scale(0.8)'
                                            }}
                                        />
                                    </div>
                                    <div className='chapter-summary-tools-text'>
                                        {isDeleting ? 'Deleting...' : 'Delete Chapter'}
                                    </div>
                                </Flex>
                            </>
                        )}
                    </div>
                )}
            </Flex>

            {/* Scene Generation Confirmation Dialog */}
            <Dialog.Root open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
                <Dialog.Content>
                    <Dialog.Title>Re-generate Scenes?</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        This chapter already has {localChapter.scenes?.length} scenes. Re-generating scenes will replace all existing scenes and their content. This action cannot be undone.
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button onClick={handleRegenerate} variant="solid" color="red">
                            Re-generate Scenes
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>

            {/* Chapter Deletion Confirmation Dialog */}
            <Dialog.Root open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <Dialog.Content>
                    <Dialog.Title>Delete Chapter?</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Are you sure you want to delete Chapter {localChapter.chapter_number}: "{localChapter.title}"?
                        {hasExistingScenes && (
                            <> This will also delete all {localChapter.scenes?.length} scenes in this chapter.</>
                        )}
                        <br /><br />
                        This action cannot be undone.
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button onClick={deleteChapter} variant="solid" color="red">
                            Delete Chapter
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default ChapterSummary;